import { CompetencyProgressTile } from '@amelio/ui-library';

import { GROWTH_PANEL_SURFACE, GROWTH_SECTION_TITLE } from './careerGrowthStyles';

import type { SkillReadinessItem } from './skillReadiness';
import type { CompetencyStatus } from '@amelio/ui-library';

import { ProficiencyIcon } from '@/components/ProficiencyIcon/ProficiencyIcon';
import { getIconEntry } from '@/constants/proficiencyIconCatalog';
import { useAmelioTranslations } from '@/hooks';
import { getLocalizedText } from '@/utils/localization';

/**
 * Where a competency stands on the tile's three-status scale. The grid overrides the label, the
 * icon and the percentage; the status still colours the ring when the catalogue level has no
 * colour of its own.
 */
const toCompetencyStatus = (fraction: number): CompetencyStatus => {
    if (fraction >= 1) {
        return 'mastered';
    }
    return fraction >= 0.5 ? 'inProgress' : 'toDevelop';
};

/**
 * The proficiency icon sits inside the ring; 20px keeps a comfortable margin from the stroke. The
 * class draws it through the library's 20px `--space-xl` token, the number gives an uploaded image
 * its intrinsic `width`/`height`: the two name the same size and change together.
 */
const LEVEL_ICON_SIZE = 20;
const LEVEL_ICON_CLASS = 'size-(--space-xl)';

interface SkillsGridProps {
    items: SkillReadinessItem[];
    /** A sentence under the title, such as "every competency has reached its level". */
    lead?: string;
    /** Defaults to the competencies of the next level. */
    title?: string;
    dataTestId?: string;
}

/**
 * The competencies of the target profile, each with how far up the proficiency scale the employee
 * already is. An unassessed competency keeps its tile, with an empty ring.
 */
export const SkillsGrid = ({
    items,
    lead,
    title,
    dataTestId = 'growth-skills-grid',
}: SkillsGridProps) => {
    const { t, i18n } = useAmelioTranslations();

    return (
        <section
            className={`${GROWTH_PANEL_SURFACE} flex w-full flex-col gap-5 px-6 py-6`}
            data-testid={dataTestId}
        >
            <div className={'flex flex-col gap-1'}>
                <h4 className={GROWTH_SECTION_TITLE}>
                    {title ?? t('performanceProfile.growth.skills.title')}
                </h4>
                {lead && <p className={'text-growth-slate text-[14px]'}>{lead}</p>}
            </div>
            <ul className={'grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3'}>
                {items.map(({ skill, level, fraction }) => (
                    <CompetencyProgressTile
                        key={skill.competencyId}
                        as={'li'}
                        title={getLocalizedText(skill.name)}
                        status={toCompetencyStatus(fraction)}
                        // An unassessed competency has no share to show, not a share of zero.
                        percentage={level ? Math.round(fraction * 100) : null}
                        statusLabel={
                            level
                                ? getLocalizedText(level.label)
                                : t('performanceProfile.growth.skills.notAssessed')
                        }
                        icon={<LevelIcon level={level} />}
                        strokeColor={level?.iconColor}
                        appearance={'soft'}
                        locale={i18n.language}
                        dataTestId={`${dataTestId}-item-${skill.competencyId}`}
                    />
                ))}
            </ul>
        </section>
    );
};

/**
 * A level's icon is a key of the module's proficiency icon catalogue (`achieved`, `notMet`…) or an
 * uploaded image -- never a name from the library's icon registry, which draws nothing for it.
 */
const LevelIcon = ({ level }: { level: SkillReadinessItem['level'] }) =>
    level && (
        <ProficiencyIcon
            iconUrl={level.iconUrl}
            iconEntry={getIconEntry(level.icon)}
            color={level.iconColor}
            className={LEVEL_ICON_CLASS}
            size={LEVEL_ICON_SIZE}
        />
    );
